import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PaperAirplaneIcon,
  TrashIcon,
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import { API_BASE_URL } from '../config/api';
import { exportAPI } from '../services/api';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';

interface HealthTopic {
  id: string;
  name: string;
  description: string;
  icon: string;
}

interface AdditionalReadingLink {
  title: string;
  url: string;
  source: string;
  description?: string;
}

interface ConsultationResult {
  session_id: string;
  topic: string;
  query: string;
  guidance: string;
  key_recommendations: string[];
  lifestyle_tips: string[];
  when_to_consult_provider: string[];
  related_topics: string[];
  follow_up_suggestions: string[];
  additional_reading: AdditionalReadingLink[];
  disclaimers: string[];
  timestamp: Date;
}

export const AIAdvisor: React.FC = () => {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Form state
  const [topic, setTopic] = useState('general');
  const [query, setQuery] = useState('');
  const [context, setContext] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [gender, setGender] = useState('');
  const [simpleMode, setSimpleMode] = useState(true);

  // UI state
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState<HealthTopic[]>([]);
  const [consultationHistory, setConsultationHistory] = useState<ConsultationResult[]>([]);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quickTips, setQuickTips] = useState<string[]>([]);

  // Age groups
  const ageGroups = [
    { id: 'child', name: t('aiAdvisor:ageGroups.child', 'Child (0-12)') },
    { id: 'teen', name: t('aiAdvisor:ageGroups.teen', 'Teen (13-17)') },
    { id: 'adult', name: t('aiAdvisor:ageGroups.adult', 'Adult (18-64)') },
    { id: 'senior', name: t('aiAdvisor:ageGroups.senior', 'Senior (65+)') },
  ];

  // Load topics on mount
  useEffect(() => {
    fetchTopics();
  }, []);

  // Load quick tips when topic changes
  useEffect(() => {
    if (topic) {
      fetchQuickTips(topic);
    }
  }, [topic]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [consultationHistory]);

  const fetchTopics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ai-advisor/topics`);
      if (response.ok) {
        const data = await response.json();
        setTopics(data.topics);
      }
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    }
  };

  const fetchQuickTips = async (topicId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/ai-advisor/quick-tips/${topicId}`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      if (response.ok) {
        const data = await response.json();
        setQuickTips(data.tips || []);
      }
    } catch (error) {
      console.error('Failed to fetch quick tips:', error);
    }
  };

  const resetForm = () => {
    setTopic('general');
    setQuery('');
    setContext('');
    setAgeGroup('');
    setGender('');
    setConsultationHistory([]);
    setActiveSessionId(null);
    setError(null);
    setFollowUpQuestion('');
  };

  const handleConsultation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query.trim()) {
      setError(t('aiAdvisor:errors.queryRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/ai-advisor/consult`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          topic,
          query,
          context: context || null,
          age_group: ageGroup || null,
          gender: gender || null,
          simple_mode: simpleMode,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get consultation');
      }

      const data = await response.json();

      const result: ConsultationResult = {
        session_id: data.session_id,
        topic,
        query,
        guidance: data.guidance,
        key_recommendations: data.key_recommendations,
        lifestyle_tips: data.lifestyle_tips,
        when_to_consult_provider: data.when_to_consult_provider,
        related_topics: data.related_topics,
        follow_up_suggestions: data.follow_up_suggestions,
        additional_reading: data.additional_reading || [],
        disclaimers: data.disclaimers,
        timestamp: new Date(),
      };

      setConsultationHistory([...consultationHistory, result]);
      setActiveSessionId(data.session_id);
      setQuery('');
      setContext('');
    } catch (error) {
      console.error('Consultation error:', error);
      setError(t('aiAdvisor:errors.consultFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = async (sessionId: string) => {
    if (!followUpQuestion.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const lastConsultation = consultationHistory.find((c) => c.session_id === sessionId);

      const response = await fetch(`${API_BASE_URL}/ai-advisor/follow-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          session_id: sessionId,
          question: followUpQuestion,
          previous_context: lastConsultation?.guidance.substring(0, 2000) || '',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get follow-up response');
      }

      const data = await response.json();

      const result: ConsultationResult = {
        session_id: sessionId,
        topic: lastConsultation?.topic || 'follow-up',
        query: `${t('aiAdvisor:followUp')}: ${followUpQuestion}`,
        guidance: data.response,
        key_recommendations: [],
        lifestyle_tips: [],
        when_to_consult_provider: [],
        related_topics: [],
        follow_up_suggestions: [],
        additional_reading: [],
        disclaimers: [data.disclaimer],
        timestamp: new Date(),
      };

      setConsultationHistory([...consultationHistory, result]);
      setFollowUpQuestion('');
    } catch (error) {
      console.error('Follow-up error:', error);
      setError(t('aiAdvisor:errors.followUpFailed'));
    } finally {
      setLoading(false);
    }
  };

  const deleteConsultation = (index: number) => {
    setConsultationHistory(consultationHistory.filter((_, i) => i !== index));
  };

  const generateExportContent = (result: ConsultationResult): string => {
    const topicName = topics.find((t) => t.id === result.topic)?.name || result.topic;
    let content = `${t('aiAdvisor:title', 'AI Health Advisor')}\n`;
    content += `${t('aiAdvisor:date', 'Date')}: ${result.timestamp.toLocaleString()}\n`;
    content += `${t('aiAdvisor:topic', 'Topic')}: ${topicName}\n`;
    content += '='.repeat(50) + '\n\n';

    content += `## ${t('aiAdvisor:yourQuestion', 'Your Question')}\n`;
    content += `${result.query}\n\n`;

    content += `## ${t('aiAdvisor:guidance', 'Guidance')}\n`;
    content += `${result.guidance}\n\n`;

    if (result.key_recommendations.length > 0) {
      content += `## ${t('aiAdvisor:keyRecommendations', 'Key Recommendations')}\n`;
      result.key_recommendations.forEach((rec) => {
        content += `- ${rec}\n`;
      });
      content += '\n';
    }

    if (result.lifestyle_tips.length > 0) {
      content += `## ${t('aiAdvisor:lifestyleTips', 'Lifestyle Tips')}\n`;
      result.lifestyle_tips.forEach((tip) => {
        content += `- ${tip}\n`;
      });
      content += '\n';
    }

    if (result.when_to_consult_provider.length > 0) {
      content += `## ${t('aiAdvisor:whenToConsult', 'When to Consult a Healthcare Provider')}\n`;
      result.when_to_consult_provider.forEach((item) => {
        content += `- ${item}\n`;
      });
      content += '\n';
    }

    content += '='.repeat(50) + '\n';
    content += `${t('aiAdvisor:disclaimer.text', 'This is health education and lifestyle guidance, not medical advice.')}\n`;

    return content;
  };

  const handleExport = async (format: 'word' | 'pdf' | 'text', index: number) => {
    const result = consultationHistory[index];
    if (!result) return;

    await exportDocument(result, format);
  };

  const handleExportForResult = (index: number) => (format: 'word' | 'pdf' | 'text') => handleExport(format, index);

  const handlePrintForResult = (index: number) => () => {
    const result = consultationHistory[index];
    if (result) {
      handlePrint(result);
    }
  };

  const handlePrint = (result: ConsultationResult) => {
    const topicName = topics.find((t) => t.id === result.topic)?.name || result.topic;
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${t('aiAdvisor:title', 'AI Health Advisor')}</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #333;
          }
          h1 { text-align: center; color: #059669; border-bottom: 2px solid #059669; padding-bottom: 10px; }
          h2 { color: #047857; margin-top: 24px; }
          .meta { text-align: center; color: #666; margin-bottom: 24px; }
          ul { margin: 0; padding-left: 20px; }
          li { margin-bottom: 8px; }
          .disclaimer { margin-top: 24px; padding: 12px; background: #fef3c7; border-radius: 8px; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>${t('aiAdvisor:title', 'AI Health Advisor')}</h1>
        <div class="meta">
          <p>${t('aiAdvisor:date', 'Date')}: ${result.timestamp.toLocaleString()}</p>
          <p>${t('aiAdvisor:topic', 'Topic')}: ${topicName}</p>
        </div>
        <h2>${t('aiAdvisor:yourQuestion', 'Your Question')}</h2>
        <p>${result.query}</p>
        <h2>${t('aiAdvisor:guidance', 'Guidance')}</h2>
        <div>${marked(result.guidance, { async: false }) as string}</div>
        ${
          result.key_recommendations.length > 0
            ? `<h2>${t('aiAdvisor:keyRecommendations', 'Key Recommendations')}</h2>
          <ul>${result.key_recommendations.map((r) => `<li>${r}</li>`).join('')}</ul>`
            : ''
        }
        ${
          result.lifestyle_tips.length > 0
            ? `<h2>${t('aiAdvisor:lifestyleTips', 'Lifestyle Tips')}</h2>
          <ul>${result.lifestyle_tips.map((t) => `<li>${t}</li>`).join('')}</ul>`
            : ''
        }
        ${
          result.when_to_consult_provider.length > 0
            ? `<h2>${t('aiAdvisor:whenToConsult', 'When to Consult a Healthcare Provider')}</h2>
          <ul>${result.when_to_consult_provider.map((w) => `<li>${w}</li>`).join('')}</ul>`
            : ''
        }
        <div class="disclaimer">
          <strong>${t('aiAdvisor:disclaimer.title', 'Important')}:</strong> ${t('aiAdvisor:disclaimer.text', 'This is health education and lifestyle guidance, not medical advice.')}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const exportDocument = async (result: ConsultationResult, format: 'word' | 'pdf' | 'text') => {
    try {
      const content = generateExportContent(result);
      const title = t('aiAdvisor:title', 'AI Health Advisor');
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `ai-advisor-${result.session_id.substring(0, 8)}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content,
        title,
        format: extension as 'docx' | 'pdf' | 'txt',
      });

      // Try File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain',
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File',
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: descriptions[format],
                accept: { [mimeTypes[extension]]: [`.${extension}`] },
              },
            ],
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name === 'AbortError') return;
        }
      }

      // Fallback download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      setError(t('aiAdvisor:errors.exportFailed'));
    }
  };

  return (
    <div className="flex flex-col h-full p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-4">
          <ChatBubbleLeftRightIcon className="h-12 w-12 text-emerald-500" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('aiAdvisor:title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">{t('aiAdvisor:subtitle')}</p>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto w-full overflow-hidden">
        {/* Left Panel - Options */}
        <div className="lg:w-1/4 flex flex-col space-y-4 overflow-y-auto">
          {/* Topic Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('aiAdvisor:form.topic')}
            </label>
            <select
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              {topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            {topic && topics.find((t) => t.id === topic) && (
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                {topics.find((t) => t.id === topic)?.description}
              </p>
            )}
          </div>

          {/* Quick Tips */}
          {quickTips.length > 0 && (
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
              <h4 className="font-medium text-emerald-800 dark:text-emerald-200 mb-2 flex items-center">
                <LightBulbIcon className="h-5 w-5 mr-2" />
                {t('aiAdvisor:quickTips')}
              </h4>
              <ul className="space-y-1">
                {quickTips.slice(0, 3).map((tip, i) => (
                  <li key={i} className="text-sm text-emerald-700 dark:text-emerald-300">
                    • {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Age Group */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('aiAdvisor:form.ageGroup')}
            </label>
            <select
              value={ageGroup}
              onChange={(e) => setAgeGroup(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">{t('aiAdvisor:form.selectAgeGroup')}</option>
              {ageGroups.map((ag) => (
                <option key={ag.id} value={ag.id}>
                  {ag.name}
                </option>
              ))}
            </select>
          </div>

          {/* Gender */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('aiAdvisor:form.gender')}
            </label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">{t('aiAdvisor:form.selectGender')}</option>
              <option value="male">{t('aiAdvisor:form.male')}</option>
              <option value="female">{t('aiAdvisor:form.female')}</option>
              <option value="other">{t('aiAdvisor:form.other')}</option>
            </select>
          </div>

          {/* Simple Mode Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('aiAdvisor:form.simpleMode')}
              </span>
              <p className="text-xs text-gray-600 dark:text-gray-300">
                {t('aiAdvisor:form.simpleModeDesc')}
              </p>
            </div>
            <button
              onClick={() => setSimpleMode(!simpleMode)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                simpleMode ? 'bg-emerald-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  simpleMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Reset Button */}
          <button
            onClick={resetForm}
            className="w-full px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {t('aiAdvisor:reset')}
          </button>
        </div>

        {/* Right Panel - Consultation */}
        <div className="lg:w-3/4 flex flex-col bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
          {/* Query Input */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <form onSubmit={handleConsultation}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('aiAdvisor:form.query')}
              </label>
              <div className="relative">
                <textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('aiAdvisor:form.queryPlaceholder')}
                  rows={3}
                  className="w-full px-4 py-3 pr-12 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 resize-none"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !query.trim()}
                  className="absolute bottom-3 right-3 p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  title={t('aiAdvisor:form.getGuidance')}
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Optional context */}
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  {t('aiAdvisor:form.context')}
                </label>
                <input
                  type="text"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder={t('aiAdvisor:form.contextPlaceholder')}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  disabled={loading}
                />
              </div>

              {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
            </form>
          </div>

          {/* Consultation History */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {consultationHistory.length === 0 ? (
              <div className="text-center py-12 text-gray-600 dark:text-gray-300">
                <HeartIcon className="h-16 w-16 mx-auto mb-4 opacity-60" />
                <p>{t('aiAdvisor:noConsultations')}</p>
                <p className="text-sm mt-2 text-gray-500 dark:text-gray-400">
                  {t('aiAdvisor:getStarted')}
                </p>
              </div>
            ) : (
              consultationHistory.map((result, index) => (
                <div
                  key={`${result.session_id}-${index}`}
                  className="bg-white dark:bg-gray-700 rounded-lg shadow p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-100 rounded">
                        {topics.find((t) => t.id === result.topic)?.name || result.topic}
                      </span>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        {result.timestamp.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <ExportDropdownMenu
                        onExport={handleExportForResult(index)}
                        onPrint={handlePrintForResult(index)}
                        showPrint={true}
                      />
                      <button
                        onClick={() => deleteConsultation(index)}
                        className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                        title={t('aiAdvisor:delete')}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      {t('aiAdvisor:yourQuestion')}:
                    </h4>
                    <p className="text-gray-700 dark:text-gray-200 text-sm italic">{result.query}</p>
                  </div>

                  <div className="prose dark:prose-invert max-w-none text-gray-900 dark:text-gray-100 prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-800 dark:prose-p:text-gray-200 prose-li:text-gray-800 dark:prose-li:text-gray-200 prose-strong:text-gray-900 dark:prose-strong:text-white">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.guidance}</ReactMarkdown>
                  </div>

                  {result.key_recommendations.length > 0 && (
                    <div className="mt-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                      <h4 className="font-medium text-emerald-800 dark:text-emerald-200 mb-2">
                        {t('aiAdvisor:keyRecommendations')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        {result.key_recommendations.map((rec, i) => (
                          <li key={i} className="text-sm text-emerald-700 dark:text-emerald-300">
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.lifestyle_tips.length > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2 flex items-center">
                        <LightBulbIcon className="h-5 w-5 mr-2" />
                        {t('aiAdvisor:lifestyleTips')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        {result.lifestyle_tips.map((tip, i) => (
                          <li key={i} className="text-sm text-blue-700 dark:text-blue-300">
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.when_to_consult_provider.length > 0 && (
                    <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <h4 className="font-medium text-red-800 dark:text-red-200 mb-2 flex items-center">
                        <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                        {t('aiAdvisor:whenToConsult')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        {result.when_to_consult_provider.map((item, i) => (
                          <li key={i} className="text-sm text-red-700 dark:text-red-300">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.follow_up_suggestions.length > 0 && (
                    <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <h4 className="font-medium text-purple-800 dark:text-purple-200 mb-2">
                        {t('aiAdvisor:suggestedFollowUps')}
                      </h4>
                      <ul className="space-y-1">
                        {result.follow_up_suggestions.map((suggestion, i) => (
                          <li
                            key={i}
                            className="text-sm text-purple-700 dark:text-purple-300 cursor-pointer hover:underline"
                            onClick={() => setFollowUpQuestion(suggestion)}
                          >
                            {suggestion}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.additional_reading && result.additional_reading.length > 0 && (
                    <div className="mt-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                      <h4 className="font-medium text-indigo-800 dark:text-indigo-200 mb-2 flex items-center">
                        <LinkIcon className="h-5 w-5 mr-2" />
                        {t('aiAdvisor:additionalReading')}
                      </h4>
                      <ul className="space-y-2">
                        {result.additional_reading.map((link, i) => (
                          <li key={i} className="text-sm">
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-700 dark:text-indigo-300 hover:underline font-medium"
                            >
                              {link.title}
                            </a>
                            <span className="text-indigo-600 dark:text-indigo-400 ml-2">
                              — {link.source}
                            </span>
                            {link.description && (
                              <p className="text-indigo-600 dark:text-indigo-400 text-xs mt-0.5">
                                {link.description}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
                    <p className="text-xs text-amber-800 dark:text-amber-200">
                      {result.disclaimers[0]}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Follow-up Input */}
          {activeSessionId && consultationHistory.length > 0 && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={followUpQuestion}
                  onChange={(e) => setFollowUpQuestion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleFollowUp(activeSessionId)}
                  placeholder={t('aiAdvisor:form.followUpPlaceholder')}
                  className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  disabled={loading}
                />
                <button
                  onClick={() => handleFollowUp(activeSessionId)}
                  disabled={loading || !followUpQuestion.trim()}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? '...' : t('aiAdvisor:form.askFollowUp')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer Footer */}
      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg max-w-7xl mx-auto w-full">
        <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
          <strong>{t('aiAdvisor:disclaimer.title')}:</strong> {t('aiAdvisor:disclaimer.text')}
        </p>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            <span className="text-gray-900 dark:text-gray-100">{t('aiAdvisor:processing')}</span>
          </div>
        </div>
      )}
    </div>
  );
};
